<script lang="ts">
  import { service } from "../services";
  import { GetCachedToken, me } from "../user";
  import { User, UserRole } from "../walletapi";
  import { onMount } from "svelte";
  import { formatMoney } from "./Formats";
  import { unassignedUsers, RefreshUnassignedUsers } from "../unassigneduser";
  import { groups } from "../grouplist";
  import { translate, currentLanguage } from "../Translator";
  import { push } from "svelte-spa-router";

  let searchUsers: User[] = [];
  let searchInput: string = "";
  let selectedUser: User | null = null;
  let showAssignGroupModal = false;
  let selectedGroupId: number | null = null;

  // Suscribirse a cambios en unassignedUsers
  $: {
    if ($unassignedUsers) {
      filterUsers();
    }
  }

  onMount(async () => {
    // Verificar autenticación primero
    if (!$me) {
      push("/user/Login");
      return;
    }

    await RefreshUnassignedUsers();
  });

  function filterUsers() {
    // Filtrar usuarios que no sean Owner
    const nonOwnerUsers = $unassignedUsers.filter(user => user.Role !== UserRole.Owner);
    
    if (!searchInput) {
      searchUsers = nonOwnerUsers;
    } else {
      const searchLower = searchInput.toLowerCase();
      searchUsers = nonOwnerUsers.filter(user =>
        user.Email?.toLowerCase().includes(searchLower) ||
        user.FirstName?.toLowerCase().includes(searchLower) ||
        user.LastName?.toLowerCase().includes(searchLower) ||
        user.Id.toString().includes(searchLower)
      );
    }
  }

  // Aplicar filtro cuando cambie el input de búsqueda
  $: {
    searchInput;
    filterUsers();
  }

  function openAssignGroupModal(user: User) {
    selectedUser = user;
    selectedGroupId = null;
    showAssignGroupModal = true;
  }

  function closeAssignGroupModal() {
    showAssignGroupModal = false;
    selectedUser = null;
    selectedGroupId = null;
  }

  // Nueva función para enviar IDs
  async function sendUserAndGroupIds() {
    if (!selectedUser || !selectedGroupId) {
      return;
    }

    const data = {
      userId: selectedUser.Id,
      groupId: selectedGroupId,
      userName: `${selectedUser.FirstName} ${selectedUser.LastName}`,
      groupName: $groups.find(g => g.Id === selectedGroupId)?.Name
    };

    try {
      const token = GetCachedToken();
      if (!token) {
        console.error('No hay token disponible');
        return;
      }

      const response = await service.walletapi.assignGroup({
        Token: token,
        Data: {
          Id: data.userId,
          GroupId: data.groupId
        }
      });

      if (response?.Success) {
        await RefreshUnassignedUsers();
        closeAssignGroupModal();
      }
    } catch (error) {
      // Error handling without console.log
    }
  }

  // Filtrar grupos disponibles según la jerarquía del usuario actual
  $: availableGroups = $groups.filter(group => {
    // if ($me?.GroupId === 1) return true; 
    if (!$me?.GroupId) return false;
    
    const myGroupId = $me.GroupId.toString();
    if (group.Id.toString() === myGroupId) return true;
    
    let currentGroup: typeof group | undefined = group;
    while (currentGroup?.ParentId) {
      if (currentGroup.ParentId.toString() === myGroupId) return true;
      currentGroup = $groups.find(g => g.Id === currentGroup.ParentId);
    }
    return false;
  });




</script>

<h1 class="ui centered dividing header">{$translate('Unassigned Users')}</h1>

<div class="ui fluid icon input">
  <input
    type="text"
    placeholder="Search users..."
    bind:value={searchInput}
  />
  <i class="search icon"></i>
</div>

{#if !searchUsers || searchUsers.length === 0}
  <div class="ui placeholder segment center">
    <div class="header">{$translate('No unassigned users found')}</div>
  </div>
{:else}
  <table class="userlist ui celled unstackable striped selectable very compact unselectable table">
    <thead>
      <tr>
        <th style="width: 0px">#</th>
        <th>{$translate('First Name')}</th>
        <th>{$translate('Last Name')}</th>
        <th>{$translate('E-mail')}</th>
        <th>{$translate('Phone Number')}</th>
        <th>{$translate('Money Balance')}</th>
        <th style="width: 100%">&nbsp;</th>
        <th style="width: 0px">{$translate('Options')}</th>
      </tr>
    </thead>
    <tbody>
      {#each searchUsers as user}
        <tr>
          <td class="numeric">{user.Id}</td>
          <td>{user.FirstName ?? ""}</td>
          <td>{user.LastName ?? ""}</td>
          <td>{user.Email ?? ""}</td>
          <td>{user.PhoneNumber ?? ""}</td>
          <td class="numeric">{formatMoney(user.LastWalletTransaction?.MoneyTransaction?.[0]?.Balance)}</td>
          <td>&nbsp;</td>
          <td>
            <button 
              class="ui primary icon small button"
              on:click={() => openAssignGroupModal(user)}
              title="Assign Group"
            >
              <i class="users icon"></i>
            </button>
          </td>
        </tr>
      {/each}
    </tbody>
  </table>
{/if}

{#if showAssignGroupModal && selectedUser}
  <div class="ui modal active">
    <div class="header">
      Assign Group to {selectedUser.FirstName} {selectedUser.LastName}
    </div>
    <div class="content">
      <div class="ui form">
        <div class="field">
          <label>Select Group</label>
          <select 
            class="ui dropdown" 
            bind:value={selectedGroupId}
            on:change={() => {
              // Group selection changed
            }}
          >
            <option value={null}>Select a group...</option>
            {#each availableGroups as group}
              <option value={group.Id}>
                {group.Name} (ID: {group.Id})
              </option>
            {/each}
          </select>
        </div>
      </div>
    </div>
    <div class="actions">
      <button class="ui button" on:click={closeAssignGroupModal}>
       {$translate('Cancel')}
      </button>
      <button 
        class="ui primary button" 
        disabled={!selectedGroupId}
        on:click={sendUserAndGroupIds}
      >
       {$translate('Assign')}
      </button>
    </div>
  </div>
  <div class="ui dimmer active" on:click={closeAssignGroupModal}></div>
{/if}

<style>
  .center {
    text-align: center;
  }

  .ui.input {
    margin-bottom: 1em;
  }

  .userlist {
    white-space: nowrap;
  }

  .userlist td {
    padding: 0.2em 0.4em !important;
  }

  .numeric {
    font-family: monospace;
    text-align: right !important;
  }

  .ui.modal {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1001;
  }

  .ui.dimmer {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.85);
    z-index: 1000;
  }
</style>
